import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LineChart from 'src/components/streametric/lineChart/LineChart';
import { PointChartType } from '../../types/point-chart';
import { SelectedInputType } from '../../types/selected-input';
import { ChartWrapper, CustomWaterLoading } from 'src/components';
import { parseISO, subHours } from 'date-fns';
import dayjs from 'dayjs';
import { TimeOptionType } from '../../types';
import { ChartData } from 'src/components/streametric/lineChart/types';
import { Box, Dialog } from '@mui/material';
import { loaderBox } from './style';
import { SessionStorageService } from 'src/features/authentication/utils';
import { getSeries, getTimestampsBasedOnTimeBox } from './utils';
import { debounce } from 'lodash';
import { getAggregationType } from './utils/getAggregationType';
import { useResponsive } from 'src/hooks/useResponsive';
import { useChartData } from '../../hooks/useChartData/useChartData';
import { useChartOptions } from '../../hooks/useChartOptions';
import { GetAllResponse } from 'src/services/sites/types';
import { useToggle } from 'src/hooks/useToggle';

const AddChartModalContent = React.lazy(
  () => import('../add-chart-modal-content/AddChartModalContent')
);

type Props = {
  pointChart: PointChartType;
  removeChart: (chartId: string) => void;
  onSelectEventHandler?: (selectedInputs: SelectedInputType[], id: string) => void;
  sites?: { value: GetAllResponse[]; loaded: boolean };
  isLocalTrends?: boolean;
  fixedSiteId?: string;
};

export const LineChartTrend = ({
  pointChart,
  removeChart,
  onSelectEventHandler,
  sites,
  isLocalTrends,
  fixedSiteId,
}: Props) => {
  const isMobile = useResponsive('down', 'md');
  const { state: isEdit, onToggle: onToggleIsEdit } = useToggle();

  const sessionStorageKey = `${pointChart.id}-timeframe`;
  const selectedBoxSessionStorageKey = `${pointChart.id}-box`;

  const [selectedBox, setSelectedBox] = useState<TimeOptionType>(() => {
    const sessionStorageSelectedBox = SessionStorageService.getRaw(selectedBoxSessionStorageKey);

    return sessionStorageSelectedBox ? (sessionStorageSelectedBox as TimeOptionType) : '1h';
  });

  const [timestamps, setTimestamps] = useState<any>(() => {
    const timestamps = {
      fromDate: dayjs(subHours(new Date(), 1)),
      toDate: dayjs(new Date()),
    };
    const sessionStorageTimestamps = SessionStorageService.getParsed(sessionStorageKey);
    const sessionStorage = {
      fromDate: dayjs(new Date(sessionStorageTimestamps.fromDate)),
      toDate: dayjs(new Date(sessionStorageTimestamps.toDate)),
    };

    if (sessionStorageTimestamps) {
      return sessionStorage;
    }
    return timestamps;
  });

  const selectedTags = pointChart?.selectedInputs;

  const selectTabletTimeOptionHandler = useCallback(
    (timeOption: TimeOptionType) => {
      setSelectedBox(timeOption);
      SessionStorageService.set(selectedBoxSessionStorageKey, timeOption);

      if (timeOption !== 'custom') {
        const timestamps = getTimestampsBasedOnTimeBox(timeOption);
        const aggType = getAggregationType(
          parseISO(timestamps.fromDate.toISOString()),
          parseISO(timestamps.toDate.toISOString()),
          timeOption
        );
        aggregationTypeRef.current = aggType;
      }
    },
    [selectedBoxSessionStorageKey]
  );

  const startDate =
    selectedBox === 'custom'
      ? new Date(timestamps.fromDate.toDate().setHours(0, 0, 0, 0)).toISOString()
      : timestamps.fromDate.toISOString();
  const endDate =
    selectedBox === 'custom'
      ? new Date(timestamps.toDate.toDate().setHours(23, 59, 59, 999)).toISOString()
      : timestamps.toDate.toISOString();

  const aggregationTypePayload = useMemo(
    () => getAggregationType(parseISO(startDate), parseISO(endDate), selectedBox),
    [startDate, endDate, selectedBox]
  );

  useEffect(() => {
    aggregationTypeRef.current = aggregationTypePayload;
  }, [aggregationTypePayload]);

  const onChangeTimestapHandler = useCallback(
    (values: [Date, Date]) => {
      setTimestamps(() => {
        const newState = { fromDate: dayjs(values[0]), toDate: dayjs(values[1]) };

        SessionStorageService.set(sessionStorageKey, JSON.stringify(newState));
        return newState;
      });
    },
    [sessionStorageKey]
  );

  const aggregationTypeRef = useRef<'two_hour' | 'five_min' | 'thirty_min' | null>(null);

  const onSelectTimestampHandler = (timeOption: TimeOptionType) => {
    setTimeout(() => {
      setTimestamps(() => {
        const newState = getTimestampsBasedOnTimeBox(timeOption);
        SessionStorageService.set(sessionStorageKey, JSON.stringify(newState));
        return newState;
      });
    }, 0);
  };

  const { data, isLoading } = useChartData({
    timestamps: timestamps,
    selectedTags: selectedTags,
    startDate: startDate,
    endDate: endDate,
    aggregationTypePayload: aggregationTypePayload,
  });

  const timestampsRef = useRef(timestamps);
  useEffect(() => {
    timestampsRef.current = timestamps;
  }, [timestamps]);

  const extendTimeframeOnZoomOutOrPan = useCallback(
    (min: string, max: string) => {
      const formatedMin = dayjs(new Date(min));
      const formatedMax = dayjs(new Date(max));
      const currentTimestamps = timestampsRef.current;

      const isOutOfCurrentScope =
        formatedMin.isBefore(currentTimestamps.fromDate) ||
        formatedMax.isAfter(currentTimestamps.toDate);
      const isDifferentFromCurrent =
        !formatedMin.isSame(currentTimestamps.fromDate) ||
        !formatedMax.isSame(currentTimestamps.toDate);

      const start = parseISO(formatedMin.toISOString());
      const end = parseISO(formatedMax.toISOString());
      const newAggregationType = getAggregationType(start, end, 'custom');
      const prevAggregationType = aggregationTypeRef.current;

      const isAggregationChanged = newAggregationType !== prevAggregationType;
      if ((isOutOfCurrentScope && isDifferentFromCurrent) || isAggregationChanged) {
        aggregationTypeRef.current = newAggregationType;
        setTimestamps({ fromDate: formatedMin, toDate: formatedMax });
        setSelectedBox('custom');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const debouncedExtendTimeframeOnZoomOutOrPan = useRef(
    debounce(extendTimeframeOnZoomOutOrPan, 800)
  ).current;

  const allSeries: ChartData[] = useMemo(
    () => getSeries(data, pointChart.selectedInputs, endDate),
    [data, pointChart.selectedInputs, endDate]
  );

  const extendedColors = useMemo(
    () =>
      selectedTags?.flatMap((tag) =>
        tag.statistic === 'Min-Average-Max'
          ? [tag.color ?? '', tag.color ?? '', tag.color ?? '']
          : [tag.color ?? '']
      ),
    [selectedTags]
  );

  const seriesAxisIds = useMemo(() => {
    let axisCounter = 0;
    return selectedTags.flatMap((tag) => {
      const axisId = axisCounter === 0 ? 'y' : `y${axisCounter - 1}`;
      axisCounter++;
      return tag.statistic === 'Min-Average-Max' ? [axisId, axisId, axisId] : [axisId];
    });
  }, [selectedTags]);

  const toDateCustom = useMemo(
    () =>
      new Date(
        dayjs(timestamps.toDate)
          .set('hour', 23)
          .set('minute', 59)
          .set('second', 59)
          .set('millisecond', 0)
          .toISOString()
      ),
    [timestamps.toDate]
  );

  const {
    chartRef,
    onResetZoomHandler,
    zoomInHandler,
    zoomOutHandler,
    isPanAndZoomActive,
    togglePanAndZoom,
    options,
    changeTimeFormatHandler,
  } = useChartOptions({
    fromDate: timestamps.fromDate,
    toDate: selectedBox === 'custom' ? toDateCustom : timestamps.toDate,
    debouncedExtendTimeframeOnZoomOutOrPan: debouncedExtendTimeframeOnZoomOutOrPan,
    tags: selectedTags,
    isMobile: isMobile,
  });

  const onResetTimestamp = useCallback(() => {
    setTimestamps(() => {
      const timestamps = {
        fromDate: dayjs(subHours(new Date(), 1)),
        toDate: dayjs(new Date()),
      };
      const sessionStorageTimestamps = SessionStorageService.getParsed(sessionStorageKey);
      const sessionStorage = {
        fromDate: dayjs(new Date(sessionStorageTimestamps.fromDate)),
        toDate: dayjs(new Date(sessionStorageTimestamps.toDate)),
      };

      if (sessionStorageTimestamps) {
        return sessionStorage;
      }
      return timestamps;
    });
    setSelectedBox(() => {
      const sessionStorageSelectedBox = SessionStorageService.getRaw(selectedBoxSessionStorageKey);
      return sessionStorageSelectedBox ? (sessionStorageSelectedBox as TimeOptionType) : '1h';
    });
  }, [sessionStorageKey, selectedBoxSessionStorageKey]);

  return (
    <>
      <ChartWrapper
        zoomOutHandler={zoomOutHandler}
        zoomInHandler={zoomInHandler}
        onResetZoomHandler={onResetZoomHandler}
        onResetTimestamp={onResetTimestamp}
        isPanAndZoomActive={isPanAndZoomActive}
        togglePanAndZoom={togglePanAndZoom}
        removeChart={removeChart}
        id={pointChart.id}
        timestamps={timestamps}
        onChangeTimestapHandler={onChangeTimestapHandler}
        onSelectTimestampHandler={onSelectTimestampHandler}
        selectTabletTimeOptionHandler={selectTabletTimeOptionHandler}
        setTimestamps={setTimestamps}
        sessionStorageKey={sessionStorageKey}
        selectedBoxSessionStorageKey={selectedBoxSessionStorageKey}
        sites={sites}
        onSelectEventHandler={onSelectEventHandler}
        pointChartData={allSeries}
        selectedBox={selectedBox}
        setSelectedBox={setSelectedBox}
        changeTimeFormatHandler={changeTimeFormatHandler}
        fixedSiteId={fixedSiteId}
        selectedInputs={isLocalTrends ? pointChart.selectedInputs : undefined}
        onEditOpen={onToggleIsEdit}
      >
        {isLoading ? (
          <Box sx={loaderBox}>
            <CustomWaterLoading />
          </Box>
        ) : null}
        <Box sx={{ opacity: isLoading ? 0 : 1 }}>
          <LineChart
            chartRef={chartRef}
            options={options}
            allSeries={allSeries}
            extendedColors={extendedColors}
            seriesAxisIds={seriesAxisIds}
          />
        </Box>
      </ChartWrapper>
      <Dialog
        open={isEdit}
        onClose={onToggleIsEdit}
        sx={{
          '& .MuiPaper-root': {
            width: '1200px',
            overflowX: 'hidden',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        <Suspense fallback={<CustomWaterLoading />}>
          <AddChartModalContent
            onToggleHandler={onToggleIsEdit}
            onSelectEventHandler={onSelectEventHandler}
            isParticularChart
            chartId={pointChart.id}
            selectedInputs={pointChart.selectedInputs}
            sites={sites}
            fixedSiteId={fixedSiteId}
          />
        </Suspense>
      </Dialog>
    </>
  );
};
export default LineChartTrend;
